// Create / edit form for one category_attributes row
// (components/AttributesEditor, on pages/CategoriesPage). Plain useState.
//
// attribute_key is set once at creation and never editable (it keys stored
// product_attributes values — changing it would orphan them), so the key
// field only shows in create mode. `options` is only relevant for
// inputType === 'select' — the rows editor appears/disappears with that
// choice, matching adminCatalogRoutes.js's categoryAttribute*Schema
// ("options must be absent unless inputType is select").

import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext.jsx';
import { Spinner } from 'shared/components/Feedback/index.js';
import { Plus, X } from 'lucide-react';
import styles from './AttributeForm.module.css';

const INPUT_TYPES = ['text', 'number', 'select', 'boolean'];

function stateFrom(initialValues) {
  return {
    attributeKey: initialValues?.attribute_key || '',
    label: initialValues?.label || '',
    labelAm: initialValues?.label_am || '',
    inputType: initialValues?.input_type || 'text',
    required: Boolean(initialValues?.required),
    sortOrder: initialValues?.sort_order != null ? String(initialValues.sort_order) : '0',
    options:
      Array.isArray(initialValues?.options) && initialValues.options.length > 0
        ? initialValues.options.map((o) => ({
            value: o.value || '',
            label: o.label || '',
            labelAm: o.labelAm || '',
          }))
        : [{ value: '', label: '', labelAm: '' }],
  };
}

export default function AttributeForm({ initialValues, onSubmit, onCancel, isPending }) {
  const { t } = useLanguage();
  const isEdit = Boolean(initialValues);
  const [values, setValues] = useState(() => stateFrom(initialValues));
  const [clientErrorId, setClientErrorId] = useState(null);

  const set = (field) => (e) =>
    setValues((prev) => ({
      ...prev,
      [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));

  const setOption = (i, key) => (e) =>
    setValues((prev) => ({
      ...prev,
      options: prev.options.map((o, idx) => (idx === i ? { ...o, [key]: e.target.value } : o)),
    }));

  const addOption = () =>
    setValues((prev) => ({
      ...prev,
      options: [...prev.options, { value: '', label: '', labelAm: '' }],
    }));

  const removeOption = (i) =>
    setValues((prev) => ({
      ...prev,
      options: prev.options.filter((_, idx) => idx !== i),
    }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setClientErrorId(null);

    if (!isEdit && !/^[a-z][a-z0-9_]*$/.test(values.attributeKey.trim())) {
      setClientErrorId('categories.attr.form.keyInvalid');
      return;
    }
    if (!values.label.trim()) {
      setClientErrorId('categories.attr.form.labelRequired');
      return;
    }

    let options;
    if (values.inputType === 'select') {
      const cleaned = values.options
        .map((o) => ({
          value: o.value.trim(),
          label: o.label.trim(),
          labelAm: o.labelAm.trim() || undefined,
        }))
        .filter((o) => o.value && o.label);
      if (cleaned.length === 0) {
        setClientErrorId('categories.attr.form.optionsRequired');
        return;
      }
      options = cleaned;
    }

    const sortOrder = Number(values.sortOrder);
    onSubmit({
      ...(isEdit ? {} : { attributeKey: values.attributeKey.trim() }),
      label: values.label.trim(),
      labelAm: values.labelAm.trim() || undefined,
      inputType: values.inputType,
      required: values.required,
      sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
      ...(values.inputType === 'select' ? { options } : {}),
    });
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <h4 className={styles.title}>
        {isEdit ? t('categories.attr.form.editTitle') : t('categories.attr.form.createTitle')}
      </h4>

      <div className={styles.grid}>
        {!isEdit && (
          <label className={styles.field}>
            {t('categories.attr.form.keyLabel')}
            <input
              type="text"
              value={values.attributeKey}
              onChange={set('attributeKey')}
              maxLength={60}
              placeholder="screen_size"
              className={`${styles.input} tabularNums`}
            />
            <span className={styles.hint}>{t('categories.attr.form.keyHint')}</span>
          </label>
        )}

        <label className={styles.field}>
          {t('categories.attr.form.labelLabel')}
          <input
            type="text"
            value={values.label}
            onChange={set('label')}
            maxLength={100}
            className={styles.input}
          />
        </label>

        <label className={styles.field}>
          {t('categories.attr.form.labelAmLabel')}
          <input
            type="text"
            value={values.labelAm}
            onChange={set('labelAm')}
            maxLength={100}
            className={styles.input}
            lang="am"
          />
        </label>

        <label className={styles.field}>
          {t('categories.attr.form.inputTypeLabel')}
          <select value={values.inputType} onChange={set('inputType')} className={styles.input}>
            {INPUT_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`categories.attr.inputType.${type}`)}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.field}>
          {t('categories.attr.form.sortOrderLabel')}
          <input
            type="number"
            value={values.sortOrder}
            onChange={set('sortOrder')}
            className={`${styles.input} tabularNums`}
          />
        </label>

        <label className={`${styles.field} ${styles.checkboxField}`}>
          <input type="checkbox" checked={values.required} onChange={set('required')} />
          {t('categories.attr.form.requiredLabel')}
        </label>
      </div>

      {values.inputType === 'select' && (
        <div className={styles.options}>
          <span className={styles.optionsTitle}>{t('categories.attr.form.optionsTitle')}</span>
          {values.options.map((opt, i) => (
            <div key={i} className={styles.optionRow}>
              <input
                type="text"
                value={opt.value}
                onChange={setOption(i, 'value')}
                placeholder={t('categories.attr.form.optionValue')}
                maxLength={60}
                className={`${styles.input} tabularNums`}
              />
              <input
                type="text"
                value={opt.label}
                onChange={setOption(i, 'label')}
                placeholder={t('categories.attr.form.optionLabel')}
                maxLength={100}
                className={styles.input}
              />
              <input
                type="text"
                value={opt.labelAm}
                onChange={setOption(i, 'labelAm')}
                placeholder={t('categories.attr.form.optionLabelAm')}
                maxLength={100}
                className={styles.input}
                lang="am"
              />
              <button
                type="button"
                className={styles.optionRemove}
                onClick={() => removeOption(i)}
                aria-label={t('categories.attr.form.optionRemove')}
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
          ))}
          <button type="button" className={styles.optionAdd} onClick={addOption}>
            <Plus size={16} aria-hidden="true" /> {t('categories.attr.form.optionAdd')}
          </button>
        </div>
      )}

      {clientErrorId && <p className={styles.error}>{t(clientErrorId)}</p>}

      <div className={styles.actions}>
        <button type="button" className={styles.cancelButton} onClick={onCancel}>
          {t('categories.attr.form.cancel')}
        </button>
        <button type="submit" className={styles.submitButton} disabled={isPending}>
          {isPending && <Spinner />}{' '}
          {isPending
            ? t('categories.attr.form.submitting')
            : isEdit
              ? t('categories.attr.form.submitEdit')
              : t('categories.attr.form.submitCreate')}
        </button>
      </div>
    </form>
  );
}
